/** Replace the nth element in the array, resulting in a new array.
 * @param arr The input array
 * @param n Index to replace, if negative will start from the end of the array
 * numbers that exceed the first or last index will be ignored and result in
 * an unchanged arrays
 * @param val The value to insert at n
 * @returns The new array with val replaced at n
 */
export const replaceNth = <T, V>(arr: T[], n: number, val: V) => {
  const sliceAt = n < 0 ? arr.length + n : n;
  if (sliceAt < 0 || sliceAt >= arr.length) {
    return arr;
  }
  return [...arr.slice(0, sliceAt), val, ...arr.slice(sliceAt + 1, arr.length)];
};

/**
 *
 * @param minimum The inclusive minimum value of the range
 * @param maximum The exclusive maximum value of the range
 * @param value The value that should cycle
 */
export const cycleIndex = (minimum: number, maximum: number, value: number) => {
  if (value < minimum) {
    return maximum - 1;
  }

  if (value >= maximum) {
    return minimum;
  }

  return value;
};

/**
 * Filters the provided array into an array of valid options.
 * Useful for ensuring that strings are actually valid Enum values,
 * and for converting from a generic string[] to a more specific Enum[]
 *
 * @param str The array to filter
 * @param validValues The set of valid options to allow
 * @returns An array of valid values
 */
export const filterArrayForValidValues = <S extends string = string>(
  arr: string[],
  validValues: Iterable<S>,
): S[] => {
  const validSet = new Set(validValues);
  return arr.flatMap((elem) => (validSet.has(elem as S) ? [elem as S] : []));
};

/**
 * Chunks an array into smaller arrays of size `size`
 *
 * @param arr The array to chunk
 * @param size The chunk size
 * @returns An array of arrays, each of size `size`
 */
export const chunkArray = <T>(arr: T[], size: number) =>
  Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
    arr.slice(i * size, i * size + size),
  );

/**
 * Sorts an array so that the item matching the primary ID appears first.
 * Useful for displaying primary items at the top of lists.
 *
 * @param items The array to sort
 * @param primaryId The ID of the primary item to move to the front
 * @param getId Function to extract the ID from each item
 * @returns A new sorted array with the primary item first
 *
 * @example
 * const users = [{id: 1, name: 'Alice'}, {id: 2, name: 'Bob'}];
 * sortByPrimary(users, 2, (user) => user.id);
 * // Returns [{id: 2, name: 'Bob'}, {id: 1, name: 'Alice'}]
 */
export const sortByPrimary = <T>(
  items: T[],
  primaryId: number | null | undefined,
  getId: (item: T) => number,
): T[] => {
  return [...items].sort((a, b) => {
    const aId = getId(a);
    const bId = getId(b);
    if (aId === primaryId) {
      return -1;
    }
    if (bId === primaryId) {
      return 1;
    }
    return 0;
  });
};
